/******************************************************************************
 *** AtrzDocPrcsMng ** 결재문서함 * 조합원/조합비 MY 결재 목록
 * 본부/중앙 : A / B / C / D
 * 지부      : E / F / G / D
*******************************************************************************/
import React from "react";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import * as Atom from "@/js/common/Atom";
import StyledTabs from "@/js/common/ui/StyledTabs";
import { Tab, TabList, TabPanel } from "react-tabs";

import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";

import AtrzDocBoxSumTab from "./tab/AtrzDocBoxSumTab";
import AtrzDocBoxFeeTab from "./tab/AtrzDocBoxFeeTab";
import AtrzDocBoxFundTab from "./tab/AtrzDocBoxFundTab";
import AtrzDocBoxMbrTab from "./tab/AtrzDocBoxMbrTab";
import AtrzDocBoxBrTab from "./tab/AtrzDocBoxBrTab";
import { systemTab } from "@/js/common/types/types";

const AtrzDocPrcsMng = () => {
    const location = useLocation();
    const userInfo = useRecoilValue(Atom.userInfo);
    const [tabIndex, setTabIndex] = React.useState<number>(0);

    // 결재 대기 건수
    const [a1SysCnt, setA1SysCnt] = React.useState<number>(0); // 총괄 교부금
    const [b1SysCnt, setB1SysCnt] = React.useState<number>(0); // 운영비
    const [c1SysCnt, setC1SysCnt] = React.useState<number>(0); // 복지기금
    const [c2SysCnt, setC2SysCnt] = React.useState<number>(0); // 자립기금
    const [c3SysCnt, setC3SysCnt] = React.useState<number>(0); // 발전기금
    const [c4SysCnt, setC4SysCnt] = React.useState<number>(0); // 희생자 부조금
    const [c5SysCnt, setC5SysCnt] = React.useState<number>(0); // 퇴직급여충당금
    const [d1SysCnt, setD1SysCnt] = React.useState<number>(0); // 조합원
    const [e1SysCnt, setE1SysCnt] = React.useState<number>(0); // 지부 운영비
    const [f1SysCnt, setF1SysCnt] = React.useState<number>(0); // 지부장 직무활동비
    const [g1SysCnt, setG1SysCnt] = React.useState<number>(0); // 지부 대회비

    const [processing, setProcessing] = React.useState<boolean>(false); // 결재 처리 중 확인
    const lastLocationKeyRef = React.useRef<string>(""); // 최초 진입시 시스템에 맞게 탭 구성

    // 특별회계기금 결재 건수 합계
    const spcSysCnt = c1SysCnt + c2SysCnt + c3SysCnt + c4SysCnt + c5SysCnt;

    // 시스템 사용 TAB
    const systemsTab: systemTab[] = [
        { id: "B", title: "운영비"           , visible: "N" },
        { id: "A", title: "총괄 교부금"      , visible: "N" },
        { id: "C", title: "특별회계기금"     , visible: "N" },
        { id: "E", title: "지부 운영비"      , visible: "N" },
        { id: "F", title: "지부장 직무활동비", visible: "N" },
        { id: "G", title: "지부 대회비"      , visible: "N" },
        { id: "D", title: "조합원"           , visible: "N" },
    ];

    const [groupTab, setGroupTab] = React.useState<systemTab[]>(systemsTab);

    // 시스템 사용 여부
    React.useEffect(() => {
        const budgetApprover = userInfo.budgetApprover; // 조합비 결재자
        const memberApprover = userInfo.memberApprover; // 조합원 결재자
        const systemAdmin    = userInfo.systemAdmin;    // 시스템 관리자
        const isBranch       = !!userInfo.ognzCd && !userInfo.ognzCd.includes("000");

        const sysCUse = [
            userInfo.sysAuthrtC1Yn,
            userInfo.sysAuthrtC2Yn,
            userInfo.sysAuthrtC3Yn,
            userInfo.sysAuthrtC4Yn,
            userInfo.sysAuthrtC5Yn
        ].some(auth => auth === "Y") ? "Y" : "N";

        const systemA = userInfo.sysAuthrtA1Yn; // 총괄 교부금
        const systemB = userInfo.sysAuthrtB1Yn; // 운영비
        const systemC = sysCUse;                // 특별회계기금
        const systemD = userInfo.sysAuthrtD1Yn; // 조합원
        const systemE = userInfo.sysAuthrtE1Yn; // 지부 운영비
        const systemF = userInfo.sysAuthrtF1Yn; // 지부장 직무활동비
        const systemG = userInfo.sysAuthrtG1Yn; // 지부 대회비

        // 초기화 후 다시 세팅
        setGroupTab(systemsTab);

        if (budgetApprover === "Y" || systemAdmin === "Y") {
            if (isBranch) {
                updateSystem("E", systemE);
                updateSystem("F", systemF);
                updateSystem("G", systemG);
            } else {
                updateSystem("A", systemA);
                updateSystem("B", systemB);
                updateSystem("C", systemC);
            }
        }

        if (memberApprover === "Y" || systemAdmin === "Y") {
            updateSystem("D", systemD);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);

    // 시스템 사용여부 변경
    const updateSystem = (targetId: string, newVal: string) => {
        setGroupTab((prev) =>
            prev.map((item) =>
                item.id === targetId ? { ...item, visible: newVal } : item
            )
        );
    };

    const visibleTabs = groupTab.filter((sys) => sys.visible === "Y");

    // 탭 구성
    React.useEffect(() => {
        if (visibleTabs.length === 0) return;
        if (lastLocationKeyRef.current === location.key) return;

        const defaultTabId = String((location.state as any)?.defaultTabId ?? "");
        if (!defaultTabId) {
            lastLocationKeyRef.current = location.key;
            return;
        }

        const idx = visibleTabs.findIndex((t) => t.id === defaultTabId);
        if (idx >= 0) setTabIndex(idx);
        else setTabIndex(0);

        lastLocationKeyRef.current = location.key;
    }, [location.key, location.state, visibleTabs]);

    // 탭 인덱스 보정
    React.useEffect(() => {
        if (tabIndex >= visibleTabs.length) {
            setTabIndex(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleTabs.length]);

    // 결재 건수 조회
    React.useEffect(() => {
        if (!userInfo?.userId) return;

        AppContext.showSpinner();
        Axios.dataAccess({
            url         : "system/atrz/select-aprvr-sys-atrz-cnt.do",
            methodType  : "post",
            paramType   : "json",
            paramData   : {
                userId: userInfo.userId,
            },
            onSuccessFn : (res: any) => {
                const item = res.result.item;

                setA1SysCnt(item.ovrlAtrzCnt    || 0);
                setB1SysCnt(item.opexpAtrzCnt   || 0);
                setC1SysCnt(item.spc01AtrzCnt   || 0);
                setC2SysCnt(item.spc02AtrzCnt   || 0);
                setC3SysCnt(item.spc03AtrzCnt   || 0);
                setC4SysCnt(item.spc04AtrzCnt   || 0);
                setC5SysCnt(item.spc05AtrzCnt   || 0);
                setD1SysCnt(item.mbrasAtrzCnt   || 0);
                setE1SysCnt(item.brOpexpAtrzCnt || 0);
                setF1SysCnt(item.brChiefAtrzCnt || 0);
                setG1SysCnt(item.eventAtrzCnt   || 0);

                AppContext.hideSpinner();
            },
        });
    }, [userInfo, processing]);

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">
                    결재 문서함
                </span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 탭 영역 */}
            {visibleTabs.length === 0 ? (
                <div className="fm-c p20 fc777777 fs16 taC mt10">
                    접근 가능한 메뉴가 없습니다.
                </div>
            ) : (
                <StyledTabs selectedIndex={tabIndex} className="mb10 tabFit">
                    <TabList>
                        {visibleTabs.map((sys, idx) => (
                            <Tab key={sys.id} onClick={() => setTabIndex(idx)}>
                                {sys.title}
                                {sys.id === "B" && b1SysCnt  > 0 && <span className="tabCnt"> ({b1SysCnt})</span>}
                                {sys.id === "A" && a1SysCnt  > 0 && <span className="tabCnt"> ({a1SysCnt})</span>}
                                {sys.id === "C" && spcSysCnt > 0 && <span className="tabCnt"> ({spcSysCnt})</span>}
                                {sys.id === "E" && e1SysCnt  > 0 && <span className="tabCnt"> ({e1SysCnt})</span>}
                                {sys.id === "F" && f1SysCnt  > 0 && <span className="tabCnt"> ({f1SysCnt})</span>}
                                {sys.id === "G" && g1SysCnt  > 0 && <span className="tabCnt"> ({g1SysCnt})</span>}
                                {sys.id === "D" && d1SysCnt  > 0 && <span className="tabCnt"> ({d1SysCnt})</span>}
                            </Tab>
                        ))}
                    </TabList>

                    {visibleTabs.map((tab) => (
                        <TabPanel key={tab.id}>
                            {tab.id === "B" && <AtrzDocBoxFeeTab processing={processing} setProcessing={setProcessing} />}
                            {tab.id === "A" && <AtrzDocBoxSumTab processing={processing} setProcessing={setProcessing} />}
                            {tab.id === "C" && <AtrzDocBoxFundTab processing={processing} setProcessing={setProcessing} />}
                            {tab.id === "E" && <AtrzDocBoxBrTab systemCode="E1" processing={processing} setProcessing={setProcessing} />}
                            {tab.id === "F" && <AtrzDocBoxBrTab systemCode="F1" processing={processing} setProcessing={setProcessing} />}
                            {tab.id === "G" && <AtrzDocBoxBrTab systemCode="G1" processing={processing} setProcessing={setProcessing} />}
                            {tab.id === "D" && <AtrzDocBoxMbrTab processing={processing} setProcessing={setProcessing} />}
                        </TabPanel>
                    ))}
                </StyledTabs>
            )}
        </div>
    );
};

export default AtrzDocPrcsMng;